import { t } from "../i18n/t";
import {
  discoverPrinter,
  isPrinterSupported,
  type PairedPrinter,
} from "./printer";
import {
  DEFAULT_TUNING,
  readLegacyTuning,
  type PrintTuning,
} from "./printSettings";

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * G-65 — which printer on this counter does what
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * A counter now has TWO cat printers: one loaded with receipt paper, one with
 * sticker paper for gift notes. They are told apart by the ROLE a person
 * assigned when pairing, never by device name — G-33 records that the owner's
 * TinyPrints are "slightly different" and their names collide, so a name-based
 * guess would eventually print a customer's receipt onto a sticker.
 *
 * PER TABLET, like the single printer before it: the machines are physically
 * on one counter. Tuning and paper width now live ON the profile rather than
 * in a second global key, which makes `printSettings.ts`'s own claim — "a
 * property of the physical machine on this counter and the roll loaded in it"
 * — true instead of aspirational. Two printers can want different heat.
 */

export type PrinterRole = "receipts" | "notes";

export const PRINTER_ROLES: PrinterRole[] = ["receipts", "notes"];

/**
 * What each role is called where a person can see it.
 *
 * G-85 S4: a FUNCTION, because this is user-facing text. As a `const` it was
 * evaluated once at import — in English, always — which is the same trap
 * `FOB_ANTENNA_HINT` and the printer's busy hint were already caught in.
 */
export function roleLabel(role: PrinterRole): string {
  return role === "receipts" ? t("printer.roleReceipt") : t("printer.roleSticker");
}

/**
 * ONE PRINTER IS THE DEFAULT SETUP (owner, 2026-09-12, after printing notes from
 * the receipt printer and finding it worked "amazingly well").
 *
 * A counter is not asked to pair two machines. Gift notes fall back to the
 * receipt printer, and the florist swaps the roll for the job — which is how the
 * shop already works. A second, dedicated sticker printer stays possible and is
 * simply an upgrade: pair one and it takes over.
 *
 * THE FALLBACK IS SYMMETRICAL (owner, 2026-10-14, G-155 / OD-250) and runs
 * ONLY when nothing is paired for the role asked for. The two kiosk doors
 * pair different roles: the office Receipts page pairs "receipts", the kiosk's
 * pairing prompts pair "notes" — so a tablet set up from the kiosk alone held a
 * sticker printer and NO ticket printer, and a receipt would have been refused
 * on a counter that prints stickers all day. Either role now covers the other
 * when it is empty; that IS the one-printer setup.
 *
 * It still cannot silently misroute: a role that HAS its own printer never
 * borrows, so a shop with two machines gets an error when the right one is
 * off, rather than a gift note quietly arriving on till roll or a receipt on
 * sticker paper. The borrowed result is flagged `shared` so the screen can
 * say which roll to load.
 */
const FALLS_BACK_TO: Partial<Record<PrinterRole, PrinterRole>> = {
  notes: "receipts",
  receipts: "notes",
};

/**
 * The tail of the Bluetooth address, which is the ONLY thing that tells two
 * of these printers apart: they are the same model and both advertise as
 * "X5", so a chip or a picker showing two identical names is a coin toss.
 * One rule, for the office printer card and the kiosk chips alike.
 */
export function printerTail(deviceId: string): string {
  const clean = deviceId.replace(/[^0-9a-f]/gi, "");
  return clean.slice(-4).toUpperCase();
}

export interface PrinterProfile extends PairedPrinter {
  tuning: PrintTuning;
  /**
   * Dots across the paper. 384 is the 58 mm roll both printers use today
   * (owner, 2026-09-12). It is stored per printer anyway because the packing
   * TRUNCATES a bitmap wider than the head with no error at all — a wrong
   * width produces a plausible job and a mangled sticker, which is exactly the
   * silent-failure class that cost an afternoon in G-64.
   */
  widthDots: number;
}

export type PrinterRegistry = Partial<Record<PrinterRole, PrinterProfile>>;

const REGISTRY_KEY = "fw-printers";
/** G-64's single-printer key. Read once to migrate; never written again. */
const LEGACY_PRINTER_KEY = "fw-receipt-printer";

export const DEFAULT_WIDTH_DOTS = 384;

function isPairedPrinter(v: Partial<PairedPrinter> | null): boolean {
  return Boolean(v?.deviceId && v.serviceUuid && v.characteristicUuid);
}

function toProfile(
  raw: Partial<PrinterProfile> | null | undefined,
): PrinterProfile | undefined {
  if (!isPairedPrinter(raw ?? null)) return undefined;
  return {
    deviceId: raw!.deviceId!,
    name: raw!.name ?? null,
    serviceUuid: raw!.serviceUuid!,
    characteristicUuid: raw!.characteristicUuid!,
    writeWithoutResponse: raw!.writeWithoutResponse === true,
    tuning: { ...DEFAULT_TUNING, ...(raw!.tuning ?? {}) },
    widthDots:
      Number.isFinite(raw!.widthDots) && raw!.widthDots! > 0
        ? raw!.widthDots!
        : DEFAULT_WIDTH_DOTS,
  };
}

/**
 * Reads G-64's single paired printer, if this tablet still only has that.
 *
 * Exported for the test; nothing else should call it.
 */
export function migrateLegacyPrinter(
  legacyPrinter: string | null,
  legacyTuning: PrintTuning,
): PrinterRegistry {
  try {
    const parsed = legacyPrinter
      ? (JSON.parse(legacyPrinter) as Partial<PairedPrinter>)
      : null;
    const profile = toProfile({
      ...parsed,
      tuning: legacyTuning,
      widthDots: DEFAULT_WIDTH_DOTS,
    });
    // The one printer a tablet already had is the RECEIPT printer: that is the
    // only thing G-64 could print. Nobody re-pairs anything.
    return profile ? { receipts: profile } : {};
  } catch {
    return {};
  }
}

export function readRegistry(): PrinterRegistry {
  let stored: string | null = null;
  try {
    stored = localStorage.getItem(REGISTRY_KEY);
  } catch {
    // Private mode or blocked storage: nothing is paired, never a crash.
    return {};
  }

  if (stored !== null) {
    try {
      const parsed = JSON.parse(stored) as Partial<
        Record<PrinterRole, Partial<PrinterProfile>>
      >;
      const out: PrinterRegistry = {};
      for (const role of PRINTER_ROLES) {
        const profile = toProfile(parsed?.[role]);
        if (profile) out[role] = profile;
      }
      return out;
    } catch {
      return {};
    }
  }

  /**
   * No registry yet — this tablet is on G-64's single-printer storage. Migrate
   * it so the owner's counter keeps printing without re-pairing anything.
   *
   * ONLY when the registry key is entirely ABSENT. `forgetPrinter` writes the
   * registry minus that role rather than deleting the key, so forgetting a
   * printer cannot resurrect it from the legacy entry on the next read.
   */
  let legacy: string | null = null;
  try {
    legacy = localStorage.getItem(LEGACY_PRINTER_KEY);
  } catch {
    /* unreadable; nothing to migrate */
  }
  const migrated = migrateLegacyPrinter(legacy, readLegacyTuning());
  writeRegistry(migrated);
  return migrated;
}

function writeRegistry(registry: PrinterRegistry): void {
  try {
    localStorage.setItem(REGISTRY_KEY, JSON.stringify(registry));
  } catch {
    /* private mode: honoured this session, forgotten on restart */
  }
  // The legacy key is deliberately LEFT IN PLACE. Tablets load the app
  // remotely from flowerworld.cc, so a frontend rollback is one deploy away
  // and the old bundle must still find its printer.
}

export function getPrinter(role: PrinterRole): PrinterProfile | null {
  return readRegistry()[role] ?? null;
}

/** Where a job for this role actually goes, and whether that was a fallback. */
export interface ResolvedPrinter {
  printer: PrinterProfile;
  /** The role it is PAIRED to, which may not be the role asked for. */
  pairedAs: PrinterRole;
  /** True when this machine is shared — the florist must check the paper. */
  shared: boolean;
}

/**
 * Pure so the rule is testable without storage. The registry goes in, the
 * decision comes out.
 */
export function resolveFromRegistry(
  registry: PrinterRegistry,
  role: PrinterRole,
): ResolvedPrinter | null {
  const own = registry[role];
  if (own) return { printer: own, pairedAs: role, shared: false };

  const fallbackRole = FALLS_BACK_TO[role];
  if (!fallbackRole) return null;
  const borrowed = registry[fallbackRole];
  if (!borrowed) return null;
  return { printer: borrowed, pairedAs: fallbackRole, shared: true };
}

export function resolvePrinterFor(role: PrinterRole): ResolvedPrinter | null {
  return resolveFromRegistry(readRegistry(), role);
}

export type PrinterStatus =
  | { state: "unsupported" }
  | { state: "unpaired" }
  | { state: "paired"; printer: PrinterProfile };

export function printerStatus(role: PrinterRole): PrinterStatus {
  if (!isPrinterSupported()) return { state: "unsupported" };
  const printer = getPrinter(role);
  return printer ? { state: "paired", printer } : { state: "unpaired" };
}

/** Pairs a device INTO a role. The person picked both. */
export async function pairPrinter(role: PrinterRole): Promise<PrinterProfile> {
  // The registry is read FIRST so the services this tablet has already
  // printed through can be offered to the browser, which is otherwise granted
  // only the four UUIDs `printer.ts` can guess at (D-28). A printer paired
  // once in the native shell therefore teaches every later browser pairing.
  const registry = readRegistry();
  const found = await discoverPrinter(
    Object.values(registry)
      .map((profile) => profile?.serviceUuid)
      .filter((uuid): uuid is string => typeof uuid === "string"),
  );
  const profile: PrinterProfile = {
    ...found,
    // Keep this role's existing heat and width: re-pairing after swapping a
    // machine should not silently undo a setting someone tuned on paper.
    tuning: registry[role]?.tuning ?? DEFAULT_TUNING,
    widthDots: registry[role]?.widthDots ?? DEFAULT_WIDTH_DOTS,
  };
  writeRegistry({ ...registry, [role]: profile });
  return profile;
}

export function forgetPrinter(role: PrinterRole): void {
  const registry = readRegistry();
  delete registry[role];
  writeRegistry(registry);
}

export function tuningFor(role: PrinterRole): PrintTuning {
  return getPrinter(role)?.tuning ?? DEFAULT_TUNING;
}

export function saveTuningFor(role: PrinterRole, tuning: PrintTuning): void {
  const registry = readRegistry();
  const printer = registry[role];
  // Nothing paired in this role: there is no machine for the setting to be
  // about, so there is nothing to save. The dials are hidden in that state.
  if (!printer) return;
  writeRegistry({ ...registry, [role]: { ...printer, tuning } });
}

/**
 * Sends bytes to whichever printer fills this role.
 *
 * The friendly "nothing paired" message names the ROLL — "no sticker printer"
 * — because that is what a florist can act on at the counter.
 */
/*
 * THERE IS DELIBERATELY NO `printTo(role, bytes)` HERE.
 *
 * There was, and `/code-review` 2026-09-12 caught what it invited: a caller
 * packs a raster for the printer IT resolved — `rowBytes` from that machine's
 * paper width, heat from that machine's tuning — and then a role-based
 * `printTo` resolved the registry a SECOND time and sent those bytes wherever
 * the second answer landed. Two answers to one question, and the failure is
 * silent: a wrong `rowBytes` produces a well-formed, wrong sticker, which is
 * the bug slice 1 already paid for once.
 *
 * So the resolution happens ONCE, in the caller, via `resolvePrinterFor`, and
 * the bytes go to that same `ResolvedPrinter.printer` through `printRaster`.
 * The device and the measurements it was measured for cannot drift apart
 * because they come from one object.
 */
